import React, { useState, useEffect } from 'react';
import { Typography } from '@material-ui/core';
import { Grid, Button, IconButton, TextField } from '@mui/material';
import { Add as AddIcon, Clear } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import BooleanSwitch from '../../../../../configured-components/form/configured-inputs/boolean/BooleanSwitch';
import FormInput from '../../../../../configured-components/form/configured-inputs/input/FormInput';
import Select from '../../../../../configured-components/form/configured-inputs/select/Select';
import { getRolesNameByRuleType, lineItemGroups, paymentAmountOptions, rateCategoryOptions, subcategories } from '../../../../../configured-components/form/configured-inputs/special/payment-line-item-rates/select-options';
import AddExistingPaymentLineItemRateDialog from '../dialogs/AddExistingPaymentLineItemRateDialog';
import { usePaymentLineItemRates } from '../PaymentLineItems.queries';

function PaymentLineItemRateForm({ name, disabledOnEdit = false }) {
    const { watch, setValue } = useFormContext();
    const [addExistingPaymentLineItemRateDialogOpen, setAddExistingPaymentLineItemRateDialogOpen] = useState(false);
    const [linkedLineItemDescription, setLinkedLineItemDescription] = useState('');
    const { data: lineItemRates } = usePaymentLineItemRates();

    const [payee, payer] = getRolesNameByRuleType(watch('ruleType'));
    const disablePayeeCategory = payee === 'Vendor';

    const linkAdvanceRate = (rate) => {
        setValue(`${name}.linked_payment_line_item_rate_id`, rate._id);
        setValue(`${name}.linked_payment_line_item_rate_reason`, 'less_advance_amount');
    };

    const removeLinkedAdvanceRate = () => {
        setLinkedLineItemDescription('');
        setValue(`${name}.linked_payment_line_item_rate_id`, null);
        setValue(`${name}.linked_payment_line_item_rate_reason`, null);
    };

    const linkedRateId = watch(`${name}.linked_payment_line_item_rate_id`);

    useEffect(() => {
        if (linkedRateId != null && lineItemRates?.length > 0) {
            const linkedRate = lineItemRates.find((rate) => rate._id.toString() === linkedRateId.toString());
            if (linkedRate) {
                setLinkedLineItemDescription(`${linkedRate.rate_name} ${linkedRate.description ? `- ${linkedRate.description}` : ''}`);
            }
        }
    }, [linkedRateId, lineItemRates]);

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="body1" className="font-bold">
                        Rate Details
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Select name={`${name}.subcategory`} props={{ label: 'Rate Type', options: subcategories, required: true, disabled: disabledOnEdit }} />
                </Grid>
                <Grid item xs={6}>
                    <FormInput name={`${name}.rate_name`} props={{ label: 'Rate Name', required: true, variant: 'outlined', disabled: disabledOnEdit }} />
                </Grid>
                <Grid item xs={6}>
                    <FormInput name={`${name}.description`} props={{ label: 'Description', required: true, variant: 'outlined', disabled: disabledOnEdit }} />
                    <Typography variant="caption" color="textSecondary">
                        Enter a clear, detailed description to help you and others identify this rate later. Descriptions also make it easier to find and understand when searching or reviewing changes.
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <FormInput name={`${name}.gl_number`} props={{ label: 'GL Number', required: false, variant: 'outlined', disabled: disabledOnEdit }} />
                </Grid>
                <Grid item xs={4}>
                    <FormInput name={`${name}.account`} props={{ label: 'Account', variant: 'outlined', disabled: disabledOnEdit }} />
                </Grid>
                <Grid item xs={4}>
                    <FormInput name={`${name}.reference`} props={{ label: 'Reference', variant: 'outlined', disabled: disabledOnEdit }} />
                </Grid>
                <Grid item xs={4}>
                    <Select name={`${name}.line_item_group`} props={{ label: 'Line Item Group', options: lineItemGroups, disabled: disabledOnEdit }} />
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ paddingTop: '20px' }}>
                <Grid item xs={12}>
                    <Typography variant="body1" className="font-bold">
                        Payment Details
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography className="font-bold">{payer}</Typography>
                    <BooleanSwitch name={`${name}.paid_by_payer`} props={{ defaultValue: true, label: `Paid by ${payer}`, required: true, disabled: disabledOnEdit }} />
                    {watch(`${name}.paid_by_payer`) && <Select name={`${name}.payer_category`} props={{ defaultValue: 'deduction', label: 'Line Placement', options: rateCategoryOptions, required: true, disabled: disabledOnEdit }} />}
                </Grid>
                <Grid item xs={6} alignItems="flex-start">
                    <Typography className="font-bold">{payee}</Typography>
                    <Select name={`${name}.paid_to_payee`} props={{ defaultValue: 'amount_paid', label: `Paid to ${payee}`, options: paymentAmountOptions, required: true, disabled: disabledOnEdit }} />
                    {watch(`${name}.paid_to_payee`) !== 'none' && (
                        <>
                            <Select name={`${name}.payee_category`} props={{ defaultValue: 'gross_pay', label: 'Line Placement', options: rateCategoryOptions, required: true, disabled: disablePayeeCategory || disabledOnEdit }} />
                        </>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <BooleanSwitch name={`${name}.hide_zero_dollar_line_item`} props={{ defaultValue: false, label: 'Hide $0.00 line item from statement', required: true, disabled: disabledOnEdit }} />
                </Grid>
                <Grid item xs={12} sx={{ paddingBottom: '20px' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={10}>
                            <TextField label="Less Payment From Advance" fullWidth disabled value={linkedLineItemDescription} />
                        </Grid>
                        <Grid item xs={2} alignItems="center" justifyContent="space-between" display="flex">
                            {linkedRateId == null && (
                                <Button
                                    aria-label="add"
                                    startIcon={<AddIcon />}
                                    variant="outlined"
                                    onClick={() => {
                                        setAddExistingPaymentLineItemRateDialogOpen(true);
                                    }}
                                    disabled={disabledOnEdit}
                                >
                                    Link&nbsp;Rate
                                </Button>
                            )}
                            {linkedRateId != null && (
                                <IconButton onClick={removeLinkedAdvanceRate} disabled={disabledOnEdit}>
                                    <Clear color="error" />
                                </IconButton>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <AddExistingPaymentLineItemRateDialog dialogOpen={addExistingPaymentLineItemRateDialogOpen} handleClose={() => setAddExistingPaymentLineItemRateDialogOpen(false)} handleAddExistingRate={linkAdvanceRate} fetchAdvanceRates />
        </>
    );
}

export default PaymentLineItemRateForm;
